import { SupplierQualAnswer } from 'types';
import { getYesNoQualQuestionType } from 'types/QualQuestion';

/**
 * Extracts the answer value from a SupplierQualAnswer object for line item questions.
 *
 * Uses answer_option_id if defined and non-zero for yes/no questions.
 * For text and numeric questions, always prefers the value field.
 *
 * Note: Only supports line item question types (text, numeric, yes/no). File upload questions
 * are not valid for line items.
 *
 * @param answer - The SupplierQualAnswer object to extract from
 * @param asString - If true, converts the result to a string. Default: false
 * @returns The answer value (number, string, null, undefined, or empty string if asString=true)
 *
 * @example
 * // Yes/No question
 * const answer = { answer_option_id: 1, value: null };
 * getAnswerValue(answer); // Returns: 1
 * getAnswerValue(answer, true); // Returns: "1"
 *
 * @example
 * // Text/Numeric question
 * const answer = { answer_option_id: undefined, value: "42" };
 * getAnswerValue(answer); // Returns: "42"
 *
 * @example
 * // Text/Numeric question with stale answer_option_id
 * const answer = { answer_option_id: 4, value: "42" };
 * getAnswerValue(answer, false, "single_text"); // Returns: "42"
 *
 * @example
 * // Empty answer
 * const answer = { answer_option_id: 0, value: null };
 * getAnswerValue(answer); // Returns: null
 * getAnswerValue(answer, true); // Returns: ""
 */
export const getAnswerValue = (
  answer: SupplierQualAnswer | undefined,
  asString = false,
  questionType?: string
): number | string | null | undefined => {
  if (!answer) {
    return asString ? '' : undefined;
  }

  const isYesNoQuestion = questionType === getYesNoQualQuestionType();
  let rawValue: number | string | null | undefined;

  if (questionType === undefined || isYesNoQuestion) {
    rawValue = answer.answer_option_id != null && answer.answer_option_id !== 0
      ? answer.answer_option_id
      : answer.value;
  } else {
    rawValue = answer.value;
  }

  // Convert to string if requested
  if (asString) {
    return String(rawValue ?? '');
  }

  return rawValue as number | string | null | undefined;
};
